import type { Rule } from '../../core/assetType.js';
import type { Severity, ValidationIssue } from '../../core/issues.js';
import type { Story } from '../../schemas/story.js';

import { locatorOf, toPointer } from '../../core/issues.js';
import { collectExitRouteTargets } from '../shared/_helpers/exitRoutes.js';

/** A gameshow only warns on an unreachable node; a story treats one as an error. */
export const reachabilityAt = (severity: Severity): Rule<Story> => ({
  code: 'story.graph.unreachable',
  check(input): ValidationIssue[] {
    const nodesById = new Map(input.nodes.map(n => [n.id, n]));
    const reachable = new Set<string>();
    const queue: string[] = [input.startNodeId];

    while (queue.length > 0) {
      const id = queue.shift()!;
      if (reachable.has(id)) {
        continue;
      }
      reachable.add(id);
      const node = nodesById.get(id);
      if (!node) {
        continue;
      }
      for (const route of Object.values(node.exitRoutes ?? {})) {
        for (const targetId of collectExitRouteTargets(route)) {
          if (!reachable.has(targetId)) {
            queue.push(targetId);
          }
        }
      }
    }

    const issues: ValidationIssue[] = [];
    input.nodes.forEach((node, nodeIdx) => {
      if (!reachable.has(node.id)) {
        const path: (string | number)[] = ['nodes', nodeIdx];
        issues.push({
          code: 'story.graph.unreachable',
          severity,
          message: `Node '${node.id}' is not reachable from startNodeId '${input.startNodeId}'.`,
          path,
          pointer: toPointer(path),
          locator: locatorOf({ nodeId: node.id, branchId: node.branchId }),
          currentValue: node.id,
          constraint: { kind: 'graph', detail: 'unreachable from start' },
        });
      }
    });
    return issues;
  },
});

export const reachability = reachabilityAt('error');
